import { ChevronLeftRounded, ChevronRightRounded, CloseRounded, ReviewsRounded, StreamRounded } from '@mui/icons-material'
import { Avatar, Box, Button, Card, Drawer, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material'
import { useCallback, useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import FlowApiClient from '../../backend/sub_api_clients/FlowApiClient'
import { IUser } from '../../definitions/Users'
import SmartCard from '../controls/cards/SmartCard'
import StreamLoader from '../loaders/StreamLoader'
import TypewriterLoader from '../loaders/TypewriterLoader'

const singleQuestions = [
    { id: 1, title: 'Summarize this settlement in plain English', text: 'Generates a narrative paragraph covering the key details: contract, performance period, interim vs. final, and the headline financials (revenue, allocation, claims, surplus/deficit).' },
    { id: 2, title: 'Does anything look unusual in these results?', text: 'Flags outliers and anomalies like unexpectedly high IBNR relative to claims, zero-value fields that seem unexpected, or a percent-of-premium that looks off.' },
    { id: 3, title: 'Break down the financial waterfall', text: 'Walks step-by-step from CMS revenue through adjustments, allocation, claims, pharmacy, IBNR, and down to the subtotal so the user can follow how the number was built.' },
    { id: 4, title: `What's driving the surplus or deficit?`, text: `Attributes the bottom-line result to its biggest contributors, quantifying each line item's share of the gap between allocation and actual costs.` },
    { id: 5, title: 'Generate talking points for a provider review meeting', text: 'Produces a concise brief framed for a JOC or quarterly review audience, translating the table into language a provider partner would understand.' },
    { id: 6, title: 'Flag any data quality concerns', text: 'Checks for zero-value fields that may warrant investigation, IBNR that looks disproportionate to claim payments, or other internal inconsistencies within the settlement output.' }
]

const comparisonQuestions = [
    { id: 1, title: 'Summarize this comparison for a stakeholder update', text: 'Plain-language narrative of the most important deltas, suitable for an email or leadership brief.' },
    { id: 2, title: 'What changed vs. the selected comparison period?', text: 'Ranks the line-item deltas by financial impact and calls out the biggest movers between the two settlements.' },
    { id: 3, title: 'Which line items are changing disproportionately to member months?', text: 'Uses the member month delta as a baseline and flags any line item whose percentage change deviates meaningfully, surfacing per-member cost shifts.' },
    { id: 4, title: 'Convert this comparison to PMPM', text: 'Divides every dollar figure by its respective member month count and presents a per-member side-by-side, stripping out volume effects.' },
    { id: 5, title: 'Is the surplus/deficit improving or worsening per member?', text: 'Compares the subtotal-to-member-month ratio across both columns to show whether the financial position is structurally better or worse, independent of membership changes.' },
    { id: 6, title: 'Are these two settlements structurally comparable?', text: 'Checks the visible metadata (performance period dates, interim vs. final, engine version, run date) and flags anything that could make a direct comparison misleading.' }
]

interface Message {
    id: string
    role: 'user' | 'assistant'
    text: string
}

interface SettlementDetailsAICardsProps {
    settlementData?: { single: string, comparison: string, isComparison: boolean }
    userInfo?: Pick<IUser, 'image' | 'name' | 'fullName'>

}

const SettlementDetailsAICards = ({ settlementData, userInfo }: SettlementDetailsAICardsProps) => {
    const [messages, setMessages] = useState<Message[]>([])
    const [loadingAI, setLoadingAI] = useState(false)
    const [openAIDrawer, setOpenAIDrawer] = useState(false)
    const [atStart, setAtStart] = useState(true)
    const [atEnd, setAtEnd] = useState(false)

    const [askMenuAnchor, setAskMenuAnchor] = useState<null | HTMLElement>(null)

    const scrollRef = useRef<HTMLDivElement>(null)
    const messagesEndRef = useRef<HTMLDivElement>(null)

    const openAskMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
        setAskMenuAnchor(e.currentTarget)
    }

    const closeAskMenu = () => {
        setAskMenuAnchor(null)
    }

    const updateLRScrollState = useCallback(() => {
        const el = scrollRef.current
        if (!el) return

        const { scrollLeft, scrollWidth, clientWidth } = el
        setAtStart(scrollLeft <= 0)
        setAtEnd(scrollLeft + clientWidth >= scrollWidth - 1)
    }, [])

    const scrollLR = useCallback((direction: 'left' | 'right') => {
        const el = scrollRef.current
        if (!el) return

        const amount = direction === 'left' ? -500 : 500
        el.scrollBy({ left: amount, behavior: 'smooth' })
    }, [])

    useEffect(() => {
        if (!openAIDrawer) return
        setTimeout(scrollBottom, 0)
    }, [openAIDrawer])

    const scrollBottom = useCallback(() => {
        const el = messagesEndRef.current
        if (!el) return

        const start = el.scrollTop
        const end = el.scrollHeight
        const distance = end - start
        const duration = 1000
        const startTime = performance.now()

        const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

        const animate = (now: number) => {
            const elapsed = now - startTime
            const progress = Math.min(elapsed / duration, 1)
            const eased = easeOutCubic(progress)

            el.scrollTop = start + distance * eased

            if (progress < 1) requestAnimationFrame(animate)
        }

        requestAnimationFrame(animate)
    }, [])

    useEffect(() => {
        const el = scrollRef.current
        if (!el) return

        updateLRScrollState()
        el.addEventListener('scroll', updateLRScrollState)
        return () => el.removeEventListener('scroll', updateLRScrollState)
    }, [updateLRScrollState])

    const fetchAIResponse = useCallback(async (conversation: Array<{ role: 'user' | 'assistant', content: Array<{ text: string }> }>, currentSettlementUUID: string, comparingSettlementUUID: string, placeholderId: string) => {
        setLoadingAI(true)

        try {
            const response = await FlowApiClient.getAIResponse(conversation, currentSettlementUUID, comparingSettlementUUID)

            const text = response?.content?.[0]?.text ?? 'No response'

            setMessages(prev =>
                prev.map(m =>
                    m.id === placeholderId
                        ? { ...m, text }
                        : m
                )
            )
        } catch (err) {
            setMessages(prev =>
                prev.map(m =>
                    m.id === placeholderId
                        ? { ...m, text: String(err?.message ?? err) }
                        : m
                )
            )
        } finally {
            setLoadingAI(false)
        }
    }, [])

    const handleAnalyze = useCallback(async (question: string, currentSettlementUUID: string, comparingSettlementUUID: string) => {
        setOpenAIDrawer(true)

        const userMessage: Message = {
            id: crypto.randomUUID(),
            role: 'user',
            text: question
        }

        setMessages(prev => [...prev, userMessage])

        const placeholderId = crypto.randomUUID()
        setMessages(prev => [
            ...prev,
            {
                id: placeholderId,
                role: 'assistant',
                text: ''
            }
        ])

        setTimeout(scrollBottom, 0)
        setLoadingAI(true)

        const conversation = [
            {
                role: 'user' as const,
                content: [{ text: question, type: 'text' }]
            }
        ]

        await fetchAIResponse(conversation, currentSettlementUUID, comparingSettlementUUID, placeholderId)
    }, [fetchAIResponse])

    const questions = settlementData?.isComparison
        ? comparisonQuestions
        : singleQuestions

    const lastAssistantId = messages.filter(m => m.role === 'assistant').at(-1)?.id

    return (
        <>
            <SmartCard sx={{ padding: 3, position: 'relative' }}>
                <Stack direction='row' justifyContent='space-between' alignItems='center'>
                    <Typography variant='h6' fontWeight={700}>
                        Analyze comparison with AI
                    </Typography>

                    <Stack direction='row' gap={1}>
                        <Button
                            variant='text'
                            size='small'
                            startIcon={<ReviewsRounded />}
                            color='secondary'
                            disabled={messages?.length === 0}
                            onClick={() => {
                                setOpenAIDrawer(true)
                            }}>Open chat
                        </Button>
                        <IconButton onClick={() => scrollLR('left')} disabled={atStart}>
                            <ChevronLeftRounded fontSize='small' />
                        </IconButton>
                        <IconButton onClick={() => scrollLR('right')} disabled={atEnd}>
                            <ChevronRightRounded fontSize='small' />
                        </IconButton>
                    </Stack>
                </Stack>

                <Stack
                    ref={scrollRef}
                    direction='row'
                    spacing={2}
                    sx={{ overflowX: 'hidden', scrollBehavior: 'smooth', paddingY: 2 }}>
                    {questions.map(item => (
                        <Box
                            key={item.id}
                            sx={{
                                'position': 'relative',
                                'minWidth': 320,
                                'padding': 2,
                                'borderRadius': 2,
                                'backgroundColor': 'transparent',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    inset: 0,
                                    borderRadius: 2,
                                    padding: 0.3,
                                    background: theme =>
                                        `radial-gradient(circle at top left, ${theme.palette.subFlow.main}, ${theme.palette.info.main})`,
                                    WebkitMask:
                                        'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                                    WebkitMaskComposite: 'xor',
                                    maskComposite: 'exclude'
                                }
                            }}>
                            <Stack spacing={2} justifyContent='space-between' height='100%'>
                                <Stack direction='row' gap={1}>
                                    <StreamRounded color='primary' />
                                    <Typography variant='subtitle1' fontWeight={600}>
                                        {item.title}
                                    </Typography>
                                </Stack>

                                <Button
                                    variant='contained'
                                    size='small'
                                    sx={{ alignSelf: 'end' }}
                                    onClick={() => handleAnalyze(`${item.title} - ${item.text}`, settlementData?.single, settlementData?.comparison)}>
                                    Analyze
                                </Button>
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            </SmartCard>

            {openAIDrawer && (
                <Drawer
                    anchor='right'
                    open={openAIDrawer}
                    onClose={() => setOpenAIDrawer(false)}
                    PaperProps={{
                        elevation: 0,
                        sx: {
                            overflow: 'hidden',
                            padding: 0,
                            borderRadius: '12px 0 0 12px',
                            width: 1200
                        }
                    }}>
                    <Stack sx={{ backgroundColor: theme => theme.palette.background.default }}>
                        <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ padding: 3 }}>
                            <Stack direction='row' alignItems='center' gap={2}>
                                <StreamRounded fontSize='large' />
                                <Stack>
                                    <Typography variant='overline'>AI CARD</Typography>
                                    <Typography variant='subtitle2' fontWeight={700}>
                                        AI Analyzing
                                    </Typography>
                                </Stack>
                            </Stack>
                            <IconButton onClick={() => setOpenAIDrawer(false)}>
                                <CloseRounded />
                            </IconButton>
                        </Stack>
                    </Stack>

                    <Stack spacing={2} sx={{ overflow: 'hidden', padding: 2, flex: 1 }}>
                        <Stack ref={messagesEndRef} spacing={3} sx={{ padding: 3, overflowY: 'auto', flex: 1 }}>
                            {messages.map(msg =>
                                msg.role === 'user'
                                    ? (
                                            <UserMessage key={msg.id} text={msg.text} avatar={userInfo?.image ?? ''} label={userInfo?.name} />
                                        )
                                    : (
                                            <AIMessage
                                                key={msg.id}
                                                text={msg.text}
                                                loading={loadingAI && msg.id === lastAssistantId}
                                            />
                                        )
                            )}
                            <div id='end-of-messages' style={{ padding: 0, margin: 0, minHeight: 0 }} />
                        </Stack>

                        <Stack alignItems='center'>
                            <Button variant='contained' startIcon={<StreamRounded />} onClick={openAskMenu}>
                                Keep asking
                            </Button>
                        </Stack>
                    </Stack>
                </Drawer>
            )}
            <Menu
                anchorEl={askMenuAnchor}
                open={Boolean(askMenuAnchor)}
                onClose={closeAskMenu}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                slotProps={{
                    paper: {
                        sx: {
                            marginTop: -1
                        }
                    }
                }}>
                {questions.map(q => (
                    <MenuItem
                        key={q.id}
                        onClick={() => {
                            closeAskMenu()
                            handleAnalyze(`${q.title} - ${q.text}`, settlementData?.single, settlementData?.comparison)
                        }}>
                        {q.title}
                    </MenuItem>
                ))}
            </Menu>

        </>
    )
}

export default SettlementDetailsAICards

const messages = [
    'Crunching member months and revenue data...',
    'Analyzing CMS revenue streams...',
    'Evaluating premium and mid/final estimates...',
    'Reviewing risk corridor adjustments...',
    'Reconciling dental and vision premium offsets...',
    'Calculating total revenue impact...',
    'Applying percent‑of‑premium allocation...',
    'Processing claim payments and adjustments...',
    'Factoring in LICS, GAP, rebates, and reinsurance...',
    'Estimating IBNR for current period...',
    'Summarizing subtotal and non‑claim expenses...',
    'Computing total expenses and YTD MLR...',
    'Determining gain or loss position...',
    'Reconciling advances paid and CMS gross...',
    'Applying actuarial adjustment factors...',
    'Adjusting Rx expense reductions...',
    'Validating all financial components...',
    'Finalizing MLR and revenue calculations...',
    'Preparing your healthcare financial summary...',
    'Almost there — generating final insights...'
]

const UserMessage = ({ text, avatar, label = 'You' }: { text: string, avatar: string, label?: string }) => (
    <Stack direction='row' spacing={2} justifyContent='flex-end' alignItems='flex-start' width='100%'>
        <Card
            elevation={1}
            sx={{
                padding: 2,
                borderRadius: 3,
                flex: 1
            }}>
            <Stack spacing={2}>
                <Typography fontWeight={700}>{label}</Typography>
                <MarkdownText text={text} />
            </Stack>
        </Card>

        <Avatar src={avatar} sx={{ width: 32, height: 32 }} />
    </Stack>
)

const AIMessage = ({ text, loading }: { text: string, loading: boolean }) => (
    <Stack direction='row' spacing={2} alignItems='flex-start' width='100%'>
        <Avatar sx={{ width: 32, height: 32, bgcolor: theme => theme.palette.primary.selected }}>
            {loading ? <StreamLoader /> : <StreamRounded color='secondary' />}
        </Avatar>

        <Card
            elevation={1}
            sx={{
                padding: 2,
                borderRadius: 3,
                flex: 1
            }}>
            <Stack spacing={2}>
                <Typography fontWeight={700}>AI Assistant</Typography>

                {loading
                    ? (
                            <TypewriterLoader messages={messages} />
                        )
                    : (
                            <MarkdownText text={text} />
                        )}
            </Stack>
        </Card>
    </Stack>
)

const MarkdownText = ({ text }: { text: string }) => (
    <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
            p: ({ children }) => (
                <Typography variant='body2' sx={{ mb: 1 }}>
                    {children}
                </Typography>
            ),
            strong: ({ children }) => (
                <Typography component='span' fontWeight={700}>
                    {children}
                </Typography>
            ),
            em: ({ children }) => (
                <Typography component='span' fontStyle='italic'>
                    {children}
                </Typography>
            ),
            ul: ({ children }) => (
                <ul style={{ marginTop: 4, marginBottom: 4 }}>{children}</ul>
            ),
            li: ({ children }) => (
                <li>
                    <Typography variant='body2' component='span'>
                        {children}
                    </Typography>
                </li>
            ),
            code: ({ children }) => (
                <Typography
                    component='code'
                    sx={{
                        padding: '2px 4px',
                        borderRadius: 1,
                        fontSize: '0.85rem'
                    }}>
                    {children}
                </Typography>
            ),
            th: ({ children }) => (
                <th style={{ textAlign: 'left', padding: '4px 8px' }}>
                    <Typography variant='body2' fontWeight={700}>
                        {children}
                    </Typography>
                </th>
            ),

            td: ({ children }) => (
                <td style={{ textAlign: 'left', padding: '4px 8px' }}>
                    <Typography variant='body2'>
                        {children}
                    </Typography>
                </td>
            )
        }}>
        {text}
    </ReactMarkdown>
)
